import React, { useMemo } from "react";

import { useIntl } from "react-intl";

import {
  HttpMetricsTimelineData,
  ObservabilityPipelineHttpMetricsOverview
} from "Api/observability/pipeline/types";
import { formatTimeFrame } from "src/organization/pages/project/environment/metrics/components/timeframeControl/util";
import useMetricsContext from "src/organization/pages/project/environment/metrics/hooks/useMetricsContext";
import {
  ChartInfo,
  HttpLayout
} from "src/organization/pages/project/environment/metrics/http2/components";
import NoData from "src/organization/pages/project/environment/metrics/http2/components/nodata";
import {
  useCursorDataSync,
  formatNumber,
  getDefaultDomain,
  getLinearAxis,
  getNextDivisible
} from "src/organization/pages/project/environment/metrics/http2/helper";
import useUpdateDimension from "src/organization/pages/project/environment/metrics/http2/hook/useUpdateDimension";
import { TopImpactValueType } from "src/organization/pages/project/environment/metrics/http2/impact/types";
import * as S from "src/organization/pages/project/environment/metrics/http2/style";
import { TimeFrameType } from "src/organization/pages/project/environment/metrics/resources/types";
import { getXTickCount } from "src/organization/pages/project/environment/metrics/utils/helper";

import Line from "../Line";
import ImpactTooltip from "../Tooltip";

const chartId = "http-url-impact";
const NUM_OF_Y_TICKS = 5;

const getDomains = (
  httpMetricsOverview:
    | ObservabilityPipelineHttpMetricsOverview
    | null
    | undefined,
  timeframe?: TimeFrameType
) => {
  const maxByUrls = [];

  (httpMetricsOverview?.data?.timelines || []).forEach(timeline => {
    Object.keys(timeline.urls ?? {}).forEach(url => {
      maxByUrls[url] = Math.max(maxByUrls[url] || 0, timeline.urls[url].count);
    });
  });

  const actualYMax = Math.max(...Object.values(maxByUrls));

  //For this graph we want to always keep the yMax to be a multiple of number of y ticks
  //This is to ensure that the y axis ticks does not have fractions
  const adjustedYMax = getNextDivisible(
    actualYMax || NUM_OF_Y_TICKS,
    NUM_OF_Y_TICKS
  );

  const defaultDomain = getDefaultDomain(timeframe);
  const domain = {
    xDomain: defaultDomain.xDomain,
    yDomain: [0, adjustedYMax]
  };
  return (httpMetricsOverview?.data?.timelines || []).length
    ? domain
    : defaultDomain;
};

type ImpactChartProps = {
  impactCount: number;
  httpMetricsOverview:
    | ObservabilityPipelineHttpMetricsOverview
    | null
    | undefined;
};

const ImpactChart = (props: ImpactChartProps) => {
  const intl = useIntl();
  const dimension = useUpdateDimension("http-url-impact");
  const { timeFrame } = useMetricsContext();

  const { httpMetricsOverview, impactCount } = props;

  const { xDomain, yDomain } = useMemo(
    () => getDomains(httpMetricsOverview, timeFrame),
    [timeFrame, httpMetricsOverview]
  );

  const { xScale, yScale, positions } = getLinearAxis({
    dimension,
    xDomain,
    yDomain
  });

  const {
    completeImpact,
    topImpact
  }: {
    completeImpact: HttpMetricsTimelineData[];
    topImpact: TopImpactValueType[][];
  } = useMemo(() => {
    const completeImpact = httpMetricsOverview?.data?.timelines || [];

    const topImpact = {};

    (httpMetricsOverview?.data?.timelines || []).forEach(timeline => {
      Object.keys(httpMetricsOverview?.data?.urls ?? {}).forEach(urlKey => {
        topImpact[urlKey] = topImpact[urlKey] || [];

        const timelineUrls = timeline.urls || {};

        topImpact[urlKey].push({
          date: timeline.date,
          method: httpMetricsOverview?.data.urls[urlKey].method,
          url: httpMetricsOverview?.data.urls[urlKey].url,
          value: timelineUrls[urlKey]?.count || 0,
          impact: timelineUrls[urlKey]?.impact || 0,
          color: httpMetricsOverview?.data.urls[urlKey].color
        });
      });
    });

    return { completeImpact, topImpact: Object.values(topImpact) };
  }, [httpMetricsOverview]);

  const disable = !completeImpact.length;
  const tickX = getXTickCount(dimension.width, timeFrame, 17);

  const cursorFuncProps = useMemo(
    () => ({
      xScale,
      yScale,
      data: completeImpact,
      bisectFn: d => d.date,
      disable,
      chartId
    }),
    [completeImpact, disable, xScale, yScale]
  );

  const { cursorSync, handleCursorMove } = useCursorDataSync({
    cursorFuncProps
  });
  const adaptedCursor = cursorSync?.adaptedCursor;

  return (
    <>
      <S.ChartTitleContainer>
        <S.ChartTitleList>
          <S.ChartTitle tag="h3">
            {intl.formatMessage(
              { id: "http.top_count_impact_url" },
              { count: impactCount }
            )}
          </S.ChartTitle>
          <ChartInfo
            title={intl.formatMessage({ id: "http.url_impact" })}
            description={intl.formatMessage({ id: "http.url_impact.info" })}
          />
          <S.ChartTitleLight className="light">
            {formatTimeFrame(timeFrame)}
          </S.ChartTitleLight>
        </S.ChartTitleList>
      </S.ChartTitleContainer>
      <HttpLayout
        chartId={chartId}
        xScale={xScale}
        yScale={yScale}
        positions={positions}
        cursorData={adaptedCursor}
        onMouseMove={handleCursorMove}
        tick={{
          x: tickX,
          y: {
            format: (value: number) => formatNumber({ value })
          }
        }}
        onMouseLeave={() => handleCursorMove?.(null)}
      >
        {disable ? (
          <NoData />
        ) : (
          topImpact.map((data, i) => (
            <Line
              index={i}
              xScale={xScale}
              yScale={yScale}
              key={i}
              data={data}
            />
          ))
        )}
      </HttpLayout>
      {adaptedCursor?.isInCursorGroup && (
        <ImpactTooltip cursorSync={cursorSync} series={topImpact} />
      )}
    </>
  );
};

export default ImpactChart;
